// Меню по правой кнопке в сетке: копировать, вставить, очистить, откуда это число.
//
// ## Почему меню тут уместно, а в списках — нет
//
// В обычном списке кабинета строка ведёт на карточку, и оттуда действие в одно нажатие: два
// нажатия всего, причём второе стоит рядом с объектом. Меню дало бы ровно те же два и отняло
// у строки её содержимое — правая кнопка в списке отдаёт браузерное «открыть в новой вкладке»,
// а это ссылка, и отнимать у неё системное меню нельзя.
//
// В сетке наоборот. Ячейка никуда не ведёт, собственного места для кнопок у неё нет (281 строка
// на 19 колонок — это пять тысяч ячеек, кнопку в каждую не положишь), а системное меню браузера
// умеет там ровно одно: скопировать выделенный ТЕКСТ. Копировать прямоугольник ячеек оно не
// умеет вовсе. То есть отнимать нечего, а дать есть что.
//
// ## Три способа открыть — и все три обязательны
//
// **Правая кнопка** — рефлекс человека, который живёт в Excel: шестнадцать листов, 281 строка,
// двадцать семь раскладок колонок.
//
// **Shift+F10 и клавиша контекстного меню** — двойник для того, кто работает без мыши. Сетка
// целиком проходится с клавиатуры (стрелки, Tab, Enter, F2, Ctrl+D), и действие, до которого с
// клавиатуры не добраться, было бы в ней дырой, а не мелочью.
//
// **Долгое нажатие и кнопка «Ещё»** — на планшете правой кнопки не существует. Ровно так же
// 10.09.2026 здесь появилась кнопка «Заполнить вниз»: до неё сетка советовала нажать Ctrl+D
// человеку, у которого нет ни Ctrl, ни D.
//
// ## Почему это НЕ `modules/settings/МенюДействий`
//
// Разница не в виде, а в слое: `shared/` не имеет права знать про `modules/` — иначе общий слой
// начинает зависеть от раздела, и вынуть раздел уже нельзя. Ровно поэтому меню здесь своё, и
// ровно поэтому его место когда-нибудь в `shared/ui/`, вместе с тем, вторым (запись об этом —
// в реестре `.claude/rules/duplication-guard.md`). Пункты и здесь, и там — СЛОВА целиком, без
// значков: «карандаш» у правки и «карандаш» у заметки не различаются вовсе.

import { Fragment, useCallback, useId, useLayoutEffect, useRef } from 'react';

import * as Popover from '@/shared/ui/popover';
import { cn } from '@/utils/cn';

/** Пункт меню. Слово целиком, без сокращений и значков. */
export interface ПунктМеню {
  подпись: string;
  выполнить: () => void;
  /**
   * Отделить чертой от предыдущего: пункт отвечает на ДРУГОЙ вопрос.
   *
   * «Откуда это число» — не работа с буфером обмена, а попытка разобраться. Стоя четвёртым в
   * ровном столбце, оно читается как четвёртый способ что-то поменять.
   */
  черта?: boolean;
}

export interface МенюЯчейкиProps {
  /** Где открыть, в координатах окна. `null` — меню закрыто. */
  точка: { x: number; y: number } | null;
  /** Что это за ячейка или сколько их выделено. Меню открыто одно, а строк 281. */
  подпись: string;
  пункты: readonly ПунктМеню[];
  /** Закрыть и вернуть фокус туда, откуда пришли. */
  закрыть: () => void;
}

export function МенюЯчейки({ точка, подпись, пункты, закрыть }: МенюЯчейкиProps) {
  // Якорь ВИРТУАЛЬНЫЙ: у меню по правой кнопке нет элемента, к которому оно прижимается, —
  // есть точка, где стоял курсор. Радикс умеет считать положение по объекту с одним методом,
  // и это дешевле собственного элемента с вычисленными координатами: тот потребовал бы
  // оформления прямо в разметке, которое в проекте разрешено ровно одному файлу.
  const якорь = useRef<{ getBoundingClientRect: () => DOMRect } | null>(null);
  якорь.current = {
    getBoundingClientRect: () => прямоугольникТочки(точка ?? { x: 0, y: 0 }),
  };

  const панель = useRef<HTMLDivElement | null>(null);
  // Опознаватель подписи: читалка должна называть меню тем же, чем названо оно на экране —
  // «Порубова С., 5 сентября», а не «меню».
  const подписьId = useId();

  // Фокус на ПЕРВЫЙ пункт, а не на саму панель. Пришедший по Shift+F10 иначе оказывается в
  // пустом слое: стрелка вниз ему ничего не даёт, а до пунктов надо ещё нажать Tab.
  useLayoutEffect(() => {
    if (точка === null) {
      return;
    }
    const первый = панель.current?.querySelector('button');
    if (первый instanceof HTMLElement) {
      первый.focus();
    }
  }, [точка]);

  /**
   * Ход по пунктам стрелками.
   *
   * Радикс даёт такой обход только выпадающему меню, а его в проекте нет — есть всплывающая
   * панель. Пятнадцать строк здесь дешевле нового пакета, а без них клавиатурный двойник
   * получился бы половинчатым: открыть можно, выбрать — только через Tab.
   */
  const поКлавише = useCallback((событие: React.KeyboardEvent<HTMLDivElement>) => {
    const шаги: Record<string, number> = { ArrowDown: 1, ArrowUp: -1 };
    const шаг = шаги[событие.key];
    const кнопки = [...(панель.current?.querySelectorAll('button') ?? [])];
    if (кнопки.length === 0) {
      return;
    }
    if (шаг !== undefined) {
      событие.preventDefault();
      const текущий = кнопки.findIndex((кнопка) => кнопка === document.activeElement);
      // По кругу: в списке из трёх пунктов упереться в край незаметно, и человек решает, что
      // клавиша не работает.
      const следующий = (текущий + шаг + кнопки.length) % кнопки.length;
      кнопки[следующий]?.focus();
      return;
    }
    if (событие.key === 'Home' || событие.key === 'End') {
      событие.preventDefault();
      (событие.key === 'Home' ? кнопки[0] : кнопки[кнопки.length - 1])?.focus();
    }
  }, []);

  return (
    <Popover.Root
      open={точка !== null}
      onOpenChange={(открыто) => {
        if (!открыто) {
          закрыть();
        }
      }}
    >
      <Popover.Anchor virtualRef={якорь} />
      <Popover.Content
        ref={панель}
        align='start'
        side='bottom'
        sideOffset={2}
        showArrow={false}
        onKeyDown={поКлавише}
        // Фокус возвращает сетка, а не набор оформления: он вернул бы его якорю, а якоря у
        // этого меню нет вовсе. Без этого человек, закрывший меню по Escape, оказывается в
        // начале страницы и ищет свою ячейку заново.
        onOpenAutoFocus={(событие) => событие.preventDefault()}
        onCloseAutoFocus={(событие) => событие.preventDefault()}
        // Появление БЕЗ движения — по той же причине, что и у меню «Ещё» в настройках: набор
        // оформления открывает панель наездом, и на планшете пункт успевает уехать из-под
        // пальца. Смысла движение здесь не несёт: меню либо открыто, либо нет.
        className='p-1 duration-0 data-[state=closed]:animate-none data-[state=open]:animate-none'
      >
        {/* Какая это ячейка. Сетка на 281 строку и 19 колонок: «копировать» без ответа на
            вопрос «что именно» — это предложение угадать. */}
        <p id={подписьId} className='px-3 pb-1 pt-2 text-paragraph-xs text-text-sub-600'>
          {подпись}
        </p>
        {/* Роли меню проставлены руками: набор оформления даёт всплывающую панель, а не меню,
            и без них читалка экрана объявляет человеку «группа из четырёх кнопок» вместо
            «меню, пункт первый из четырёх». Обход стрелками здесь уже свой — см. `поКлавише`,
            — то есть поведение меню есть, а объявления его не было. */}
        <div role='menu' aria-labelledby={подписьId} className='flex min-w-52 flex-col'>
          {пункты.map((пункт) => (
            <Fragment key={пункт.подпись}>
              {пункт.черта === true && <div className='my-1 h-px bg-stroke-soft-200' />}
              <button
                type='button'
                role='menuitem'
                onClick={() => {
                  // Закрываем ДО действия и без единого ожидания между нажатием и работой с
                  // буфером обмена: браузер разрешает читать буфер только внутри жеста
                  // человека, а жест кончается на первом же `await`. Поймано не тестом —
                  // тестам буфер подменяют, и там это работает всегда.
                  закрыть();
                  пункт.выполнить();
                }}
                // Честные 44 точки высоты, а не невидимый прямоугольник цели касания: в
                // вертикальном списке такие прямоугольники налезают друг на друга, и нажатие
                // по «Очистить» попадает в соседний пункт.
                className={cn(
                  'flex min-h-11 items-center rounded-lg px-3 text-left text-paragraph-sm',
                  'text-text-strong-950 transition-colors hover:bg-bg-weak-50',
                  'outline-none focus-visible:bg-bg-weak-50',
                )}
              >
                {пункт.подпись}
              </button>
            </Fragment>
          ))}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

/**
 * Точка как прямоугольник нулевого размера.
 *
 * Своей сборкой, а не `new DOMRect`: конструктор есть не в каждом окружении, в котором гоняют
 * проверки, а падение здесь выглядело бы поломкой меню, а не окружения.
 */
function прямоугольникТочки(точка: { x: number; y: number }): DOMRect {
  return {
    x: точка.x,
    y: точка.y,
    left: точка.x,
    right: точка.x,
    top: точка.y,
    bottom: точка.y,
    width: 0,
    height: 0,
    toJSON: () => ({}),
  } as DOMRect;
}
